import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import { Add as AddIcon, ChevronLeft, ChevronRight, Delete as DeleteIcon } from "@mui/icons-material";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from "date-fns";
import { retrieveTravelCalendarEntriesByUserIdApi } from "../../../components/api/TravelCalendarApiService";

// Compute annual leave day counts from a list of entries.
// Groups by date so a day with FIRST + LAST half-entries is counted as 1 full day, not 2.
const computeAnnualLeaveCounts = (allEntries) => {
  const sumByDate = (entries) => {
    const byDate = {};
    entries.forEach((e) => {
      const dateKey = e.entryDate ? String(e.entryDate).substring(0, 10) : "unknown";
      const weight = e.halfDayType === "FIRST" || e.halfDayType === "LAST" ? 0.5 : 1.0;
      byDate[dateKey] = Math.min(1.0, (byDate[dateKey] || 0) + weight);
    });
    return Object.values(byDate).reduce((sum, w) => sum + w, 0);
  };

  return {
    actualDays: sumByDate(allEntries.filter((e) => e.category === "ANNUAL_LEAVE" && e.entryType === "ACTUAL")),
    approvedDays: sumByDate(allEntries.filter((e) => e.category === "ANNUAL_LEAVE" && e.approvalStatus === "APPROVED")),
  };
};

// --- Annual Leave summary banner ---
const AnnualLeaveCountBanner = ({ userId }) => {
  const [counts, setCounts] = React.useState(null);

  React.useEffect(() => {
    if (!userId) return;
    setCounts(null);
    retrieveTravelCalendarEntriesByUserIdApi(userId)
      .then((res) => setCounts(computeAnnualLeaveCounts(res.data || [])))
      .catch(() => {});
  }, [userId]);

  if (!counts) return null;

  const fmt = (n) => (n % 1 === 0 ? String(n) : n.toFixed(1));

  return (
    <Alert
      severity="info"
      icon={<BeachAccessIcon fontSize="inherit" />}
      sx={{ py: 0.5, "& .MuiAlert-message": { width: "100%" } }}
    >
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <span>
          <strong>{fmt(counts.actualDays)}</strong> actual annual leave day{counts.actualDays !== 1 ? "s" : ""} already recorded
        </span>
        <span>
          <strong>{fmt(counts.approvedDays)}</strong> approved annual leave day{counts.approvedDays !== 1 ? "s" : ""} so far
        </span>
      </Box>
    </Alert>
  );
};

// --- Shared entry form fields (used in both Add/Edit and Multi-Add) ---
const EntryFormFields = React.memo(({ formData, onChange, userId }) => {
  const update = React.useCallback(
    (field, value) => onChange((prev) => ({ ...prev, [field]: value })),
    [onChange]
  );

  const flights = formData.flights && formData.flights.length > 0
    ? formData.flights
    : [{ destination: formData.destination || "", flight: formData.flight || "", etd: formData.etd || "", eta: formData.eta || "" }];

  const updateFlight = React.useCallback(
    (index, field, value) => {
      onChange((prev) => {
        const prevFlights = prev.flights && prev.flights.length > 0
          ? prev.flights
          : [{ destination: prev.destination || "", flight: prev.flight || "", etd: prev.etd || "", eta: prev.eta || "" }];
        const updated = prevFlights.map((f, i) => i === index ? { ...f, [field]: value } : f);
        return { ...prev, flights: updated };
      });
    },
    [onChange]
  );

  const addFlight = React.useCallback(
    (e) => {
      e.stopPropagation();
      onChange((prev) => {
        const prevFlights = prev.flights && prev.flights.length > 0
          ? prev.flights
          : [{ destination: prev.destination || "", flight: prev.flight || "", etd: prev.etd || "", eta: prev.eta || "" }];
        return { ...prev, flights: [...prevFlights, { destination: "", flight: "", etd: "", eta: "" }] };
      });
    },
    [onChange]
  );

  const removeFlight = React.useCallback(
    (index) => {
      onChange((prev) => {
        const prevFlights = prev.flights && prev.flights.length > 0 ? prev.flights : [];
        const updated = prevFlights.filter((_, i) => i !== index);
        return { ...prev, flights: updated.length > 0 ? updated : [{ destination: "", flight: "", etd: "", eta: "" }] };
      });
    },
    [onChange]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small" required>
          <InputLabel>Category</InputLabel>
          <Select
            value={formData.category || ""}
            onChange={(e) => update("category", e.target.value)}
            label="Category"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            <MenuItem value="ANNUAL_LEAVE">Annual Leave</MenuItem>
            <MenuItem value="BUSINESS_TRIP">Business Trip</MenuItem>
            <MenuItem value="NATIONAL_HOLIDAY">National Holiday</MenuItem>
            <MenuItem value="COMPENSATION">Compensation</MenuItem>
            <MenuItem value="OTHER">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {formData.category === "ANNUAL_LEAVE" && (
        <Grid item xs={12}>
          <AnnualLeaveCountBanner userId={userId} />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Day Type</InputLabel>
          <Select
            value={formData.halfDayType || "NONE"}
            onChange={e => update("halfDayType", e.target.value)}
            label="Day Type"
          >
            <MenuItem value="NONE">Full Day</MenuItem>
            <MenuItem value="FIRST">First Half</MenuItem>
            <MenuItem value="LAST">Last Half</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Flight rows */}
      {flights.map((fl, index) => (
        <Grid item xs={12} key={index}>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 1.5,
              position: "relative",
              backgroundColor: index === 0 ? "#fafafa" : "#f3f8ff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: "bold", color: "#555" }}>
                {index === 0 ? "Flight 1" : `Flight ${index + 1}`}
              </Typography>
              {flights.length > 1 && (
                <Tooltip title="Remove this flight" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => removeFlight(index)}
                    sx={{ ml: "auto", color: "error.main", p: 0.3 }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Destination"
                  size="small"
                  fullWidth
                  value={fl.destination}
                  onChange={(e) => updateFlight(index, "destination", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Flight"
                  size="small"
                  fullWidth
                  value={fl.flight}
                  onChange={(e) => updateFlight(index, "flight", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ETD (Time)"
                  size="small"
                  fullWidth
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  value={fl.etd}
                  onChange={(e) => updateFlight(index, "etd", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ETA (Time)"
                  size="small"
                  fullWidth
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  value={fl.eta}
                  onChange={(e) => updateFlight(index, "eta", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addFlight}
          sx={{ borderStyle: "dashed" }}
        >
          Add another flight
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Comment"
          size="small"
          fullWidth
          multiline
          rows={2}
          value={formData.comment}
          onChange={(e) => update("comment", e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Others"
          size="small"
          fullWidth
          multiline
          rows={2}
          value={formData.others}
          onChange={(e) => update("others", e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.inEu}
              onChange={(e) => update("inEu", e.target.checked)}
            />
          }
          label="in EU"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.inSin}
              onChange={(e) => update("inSin", e.target.checked)}
            />
          }
          label="in Sin"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Special Occasion"
          size="small"
          fullWidth
          value={formData.specialOccasion}
          onChange={(e) => update("specialOccasion", e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.entryType}
            onChange={(e) => update("entryType", e.target.value)}
            label="Type"
          >
            <MenuItem value="ACTUAL">Actual</MenuItem>
            <MenuItem value="TEMPLATE">Template</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {formData.specialOccasion && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: formData.sharedSpecialOccasion ? "#f3e5f5" : "#f5f5f5",
              borderRadius: 2,
              px: 2,
              py: 0.5,
              border: "1px solid",
              borderColor: formData.sharedSpecialOccasion ? "#ce93d8" : "#e0e0e0",
              transition: "all 0.2s ease",
            }}
          >
            <Typography variant="body2" sx={{ flexGrow: 1, color: "#555" }}>
              Show Special Occasion on everyone's calendar
            </Typography>
            <Switch
              checked={formData.sharedSpecialOccasion}
              onChange={(e) => update("sharedSpecialOccasion", e.target.checked)}
              color="secondary"
              size="small"
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
});

// --- Add/Edit single entry dialog (manages its own form state to avoid parent re-renders) ---
export const EntryDialog = ({
  open,
  editingEntry,
  selectedDate,
  selectedUser,
  formData: externalFormData,
  onFormChange,
  onSave,
  onClose,
}) => {
  const [localData, setLocalData] = React.useState(externalFormData);

  // Sync external → local when the dialog opens or the initial data changes
  React.useEffect(() => {
    if (open) setLocalData(externalFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = React.useCallback((updater) => {
    setLocalData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      onFormChange(next);
      return next;
    });
  }, [onFormChange]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingEntry ? "Edit Calendar Entry" : "Add Calendar Entry"}
        {selectedDate && (
          <Typography variant="subtitle2" color="textSecondary">
            {format(selectedDate, "dd MMMM yyyy, EEEE")} - {selectedUser?.name || selectedUser?.username || ""}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <EntryFormFields formData={localData} onChange={handleChange} userId={selectedUser?.id} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>
          {editingEntry ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Multi-Add dialog (manages its own form state to avoid parent re-renders) ---
export const MultiAddDialog = ({
  open,
  selectedUser,
  multiAddMonth,
  multiSelectedDates,
  multiFormData: externalFormData,
  multiAddEntries,
  onMonthChange,
  onToggleDate,
  onFormChange,
  onSave,
  onClose,
  onSetSelectedDates,
}) => {
  const [localData, setLocalData] = React.useState(externalFormData);

  React.useEffect(() => {
    if (open) setLocalData(externalFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = React.useCallback((updater) => {
    setLocalData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      onFormChange(next);
      return next;
    });
  }, [onFormChange]);
  const multiAddHasEntry = (dateStr) => {
    return multiAddEntries.some((e) => {
      const d = e.entryDate ? format(new Date(e.entryDate), "yyyy-MM-dd") : "";
      return d === dateStr;
    });
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(multiAddMonth),
    end: endOfMonth(multiAddMonth),
  });

  const handleSelectWeekdays = () => {
    const weekdays = daysInMonth
      .filter((d) => !isWeekend(d))
      .filter((d) => !multiAddHasEntry(format(d, "yyyy-MM-dd")))
      .map((d) => format(d, "yyyy-MM-dd"));
    onSetSelectedDates((prev) => [...new Set([...prev, ...weekdays])]);
  };

  const handleSelectAll = () => {
    const allDates = daysInMonth
      .filter((d) => !multiAddHasEntry(format(d, "yyyy-MM-dd")))
      .map((d) => format(d, "yyyy-MM-dd"));
    onSetSelectedDates((prev) => [...new Set([...prev, ...allDates])]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add Entry to Multiple Dates
        <Typography variant="subtitle2" color="textSecondary">
          {selectedUser?.name || selectedUser?.username || ""}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* Month navigation */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={() => onMonthChange(-1)}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="subtitle1" sx={{ minWidth: 160, textAlign: "center", fontWeight: "bold" }}>
              {format(multiAddMonth, "MMMM yyyy")}
            </Typography>
            <IconButton size="small" onClick={() => onMonthChange(1)}>
              <ChevronRight />
            </IconButton>
          </Box>

          {/* Date selection grid */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Select dates:</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
            {daysInMonth.map((date) => {
              const dateStr = format(date, "yyyy-MM-dd");
              const isSelected = multiSelectedDates.includes(dateStr);
              const wknd = isWeekend(date);
              const hasEntry = multiAddHasEntry(dateStr);
              return (
                <Tooltip
                  key={dateStr}
                  title={hasEntry ? "Already has an entry" : ""}
                  arrow
                  placement="top"
                  disableHoverListener={!hasEntry}
                >
                  <span>
                    <Chip
                      label={format(date, "dd")}
                      size="small"
                      onClick={hasEntry ? undefined : () => onToggleDate(date)}
                      disabled={hasEntry}
                      variant={isSelected ? "filled" : "outlined"}
                      color={isSelected ? "primary" : "default"}
                      sx={{
                        minWidth: 40,
                        fontWeight: isSelected ? "bold" : "normal",
                        backgroundColor: isSelected ? undefined : wknd ? "#e3f2fd" : undefined,
                        borderColor: hasEntry ? "#ed6c02" : undefined,
                        borderWidth: hasEntry ? 2 : undefined,
                        opacity: hasEntry ? 0.7 : 1,
                        position: "relative",
                        "&.Mui-disabled": {
                          opacity: 0.7,
                          borderColor: "#ed6c02",
                          borderWidth: 2,
                          color: "#ed6c02",
                        },
                        "&::after": hasEntry
                          ? {
                              content: '""',
                              position: "absolute",
                              top: 3,
                              right: 3,
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "#ed6c02",
                            }
                          : {},
                      }}
                    />
                  </span>
                </Tooltip>
              );
            })}
          </Box>

          {/* Quick select buttons */}
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button size="small" variant="text" onClick={handleSelectWeekdays}>
              All Weekdays
            </Button>
            <Button size="small" variant="text" onClick={handleSelectAll}>
              All Days
            </Button>
            <Button size="small" variant="text" onClick={() => onSetSelectedDates([])}>
              Clear
            </Button>
            <Typography variant="body2" sx={{ ml: "auto", alignSelf: "center", color: "#666" }}>
              {multiSelectedDates.length} date(s) selected
            </Typography>
          </Box>

          {/* Entry fields */}
          <EntryFormFields formData={localData} onChange={handleChange} userId={selectedUser?.id} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={multiSelectedDates.length === 0}
        >
          Save ({multiSelectedDates.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
